// Templates: ready-made boards and layouts, written in the spec format so people (the gallery)
// and agents (the `templates` tool) insert exactly the same thing.

import type { NodeSpec } from './spec.ts';

export interface Template {
  id: string;
  name: string;
  description: string;
  kind: 'WHITEBOARD' | 'DESIGN';
  /** Specs for the template with its top-left corner at (x, y). */
  build(x: number, y: number): NodeSpec[];
}

const sticky = (text: string, color: string): NodeSpec => ({ type: 'sticky', text, fill: color, width: 200, height: 200 });

/** A titled column of stickies (retro, kanban). */
function column(name: string, color: string, notes: string[], header: string): NodeSpec {
  return {
    type: 'frame',
    name,
    layout: 'column',
    gap: 16,
    padding: 24,
    radius: 16,
    fill: header,
    width: 248,
    children: [{ type: 'text', text: name, fontSize: 22, fontWeight: 700 }, ...notes.map((t) => sticky(t, color))],
  } as NodeSpec;
}

export const TEMPLATES: Template[] = [
  {
    id: 'retro',
    name: 'Retrospective',
    description: 'What went well, what didn’t, and action items',
    kind: 'WHITEBOARD',
    build: (x, y) => [
      {
        type: 'section',
        name: 'Retrospective',
        x,
        y,
        width: 900,
        height: 760,
        children: [
          { ...column('Went well', 'green', ['Shipped on time', 'Great pairing'], '#e9f9ec'), x: 40, y: 80 },
          { ...column('Didn’t go well', 'red', ['Flaky tests', 'Late design changes'], '#fdecea'), x: 326, y: 80 },
          { ...column('Action items', 'blue', ['Fix CI flakes', 'Design review earlier'], '#e8f4ff'), x: 612, y: 80 },
        ],
      } as NodeSpec,
    ],
  },
  {
    id: 'brainstorm',
    name: 'Brainstorm',
    description: 'A question in the middle and room for ideas around it',
    kind: 'WHITEBOARD',
    build: (x, y) => {
      const cx = x + 500,
        cy = y + 400;
      const ideas: NodeSpec[] = Array.from({ length: 8 }, (_, i) => {
        const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
        return { ...sticky(i === 0 ? 'Add your idea' : '', ['yellow', 'orange', 'pink', 'violet', 'blue', 'teal', 'green', 'yellow'][i]), x: Math.round(cx + Math.cos(a) * 340 - 100), y: Math.round(cy + Math.sin(a) * 300 - 100) };
      });
      return [{ type: 'section', name: 'Brainstorm', x, y, width: 1000, height: 800, fill: '#fafafa' } as NodeSpec, { type: 'shape', shape: 'ellipse', x: cx - 150, y: cy - 90, width: 300, height: 180, fill: '#1e1e1e', color: '#ffffff', text: 'How might we…?', fontSize: 24 } as NodeSpec, ...ideas];
    },
  },
  {
    id: 'kanban',
    name: 'Kanban',
    description: 'To do, doing and done columns',
    kind: 'WHITEBOARD',
    build: (x, y) => [
      { ...column('To do', 'yellow', ['Write spec', 'Set up analytics'], '#f5f5f5'), x, y },
      { ...column('Doing', 'blue', ['Build onboarding'], '#f5f5f5'), x: x + 280, y },
      { ...column('Done', 'green', ['Kickoff'], '#f5f5f5'), x: x + 560, y },
    ],
  },
  {
    id: 'flowchart',
    name: 'Flowchart',
    description: 'Start, steps, a decision and two outcomes',
    kind: 'WHITEBOARD',
    build: (x, y) => [
      { type: 'shape', ref: 'start', shape: 'ellipse', text: 'Start', x, y: y + 120, width: 160, height: 80, fill: '#cdf4d3' } as NodeSpec,
      { type: 'shape', ref: 'step', shape: 'rounded_rectangle', text: 'Do the thing', x: x + 240, y: y + 120, width: 180, height: 80, fill: '#c2e5ff' } as NodeSpec,
      { type: 'shape', ref: 'check', shape: 'diamond', text: 'Worked?', x: x + 500, y: y + 100, width: 160, height: 120, fill: '#fff2b2' } as NodeSpec,
      { type: 'shape', ref: 'yes', shape: 'rounded_rectangle', text: 'Ship it', x: x + 760, y: y + 120, width: 160, height: 80, fill: '#cdf4d3' } as NodeSpec,
      { type: 'shape', ref: 'no', shape: 'rounded_rectangle', text: 'Try again', x: x + 500, y: y + 320, width: 160, height: 80, fill: '#ffc7c2' } as NodeSpec,
      { type: 'connector', from: 'start', to: 'step' } as NodeSpec,
      { type: 'connector', from: 'step', to: 'check' } as NodeSpec,
      { type: 'connector', from: 'check', to: 'yes', text: 'Yes' } as NodeSpec,
      { type: 'connector', from: 'check', to: 'no', text: 'No' } as NodeSpec,
    ],
  },
  {
    id: 'journey',
    name: 'User journey map',
    description: 'Stages across, what people do, think and feel down',
    kind: 'WHITEBOARD',
    build: (x, y) => {
      const stages = ['Discover', 'Sign up', 'First use', 'Come back'];
      const rows = ['Doing', 'Thinking', 'Feeling', 'Opportunities'];
      const cell = (text: string, head: boolean, fill: string): NodeSpec => ({ type: 'frame', name: 'Cell', layout: 'row', width: 220, height: head ? 56 : 160, padding: 14, fill, stroke: '#e6e6e6', strokeAlign: 'inside', children: [{ type: 'text', text, width: 'fill', fontSize: head ? 16 : 14, fontWeight: head ? 700 : 400 }] }) as NodeSpec;
      return [
        {
          type: 'frame',
          name: 'User journey map',
          x,
          y,
          layout: 'column',
          radius: 12,
          clip: true,
          fill: '#ffffff',
          stroke: '#e6e6e6',
          children: [
            { type: 'frame', name: 'Stages', layout: 'row', fill: null, children: [cell('', true, '#f5f5f5'), ...stages.map((s) => cell(s, true, '#f5f5f5'))] },
            ...rows.map((r) => ({ type: 'frame', name: r, layout: 'row', fill: null, children: [cell(r, true, '#fafafa'), ...stages.map(() => cell('', false, '#ffffff'))] })),
          ],
        } as NodeSpec,
      ];
    },
  },
  {
    id: 'matrix',
    name: '2×2 prioritization',
    description: 'Impact versus effort quadrants',
    kind: 'WHITEBOARD',
    build: (x, y) => {
      const q = (name: string, qx: number, qy: number, fill: string): NodeSpec => ({ type: 'frame', name, x: x + qx, y: y + qy, width: 400, height: 320, fill, children: [{ type: 'text', text: name, x: 20, y: 16, fontSize: 18, fontWeight: 700 }] }) as NodeSpec;
      return [
        q('Quick wins', 60, 0, '#e9f9ec'),
        q('Big bets', 460, 0, '#e8f4ff'),
        q('Fill-ins', 60, 320, '#f5f5f5'),
        q('Money pits', 460, 320, '#fdecea'),
        { type: 'connector', from: { x: x + 40, y: y + 640 }, to: { x: x + 40, y: y }, line: 'straight' } as NodeSpec,
        { type: 'connector', from: { x: x + 60, y: y + 660 }, to: { x: x + 860, y: y + 660 }, line: 'straight' } as NodeSpec,
        { type: 'text', text: 'Impact', x: x - 30, y: y + 300, fontSize: 14, fontWeight: 600 } as NodeSpec,
        { type: 'text', text: 'Effort', x: x + 440, y: y + 670, fontSize: 14, fontWeight: 600 } as NodeSpec,
      ];
    },
  },
  {
    id: 'meeting',
    name: 'Meeting notes',
    description: 'Agenda, notes, decisions and action items',
    kind: 'WHITEBOARD',
    build: (x, y) => [
      {
        type: 'frame',
        name: 'Meeting notes',
        x,
        y,
        width: 640,
        layout: 'column',
        gap: 18,
        padding: 32,
        radius: 16,
        fill: '#ffffff',
        stroke: '#e6e6e6',
        children: [
          { type: 'text', text: 'Weekly sync', fontSize: 28, fontWeight: 700 },
          { type: 'text', text: 'Agenda', fontSize: 16, fontWeight: 700 },
          { type: 'text', text: '1. Updates\n2. Blockers\n3. Next steps', markdown: true, width: 'fill', fontSize: 15 },
          { type: 'text', text: 'Decisions', fontSize: 16, fontWeight: 700 },
          { type: 'text', text: '- Decision', markdown: true, width: 'fill', fontSize: 15 },
          { type: 'text', text: 'Action items', fontSize: 16, fontWeight: 700 },
          { type: 'text', text: '- Owner: task', markdown: true, width: 'fill', fontSize: 15 },
        ],
      } as NodeSpec,
    ],
  },
  {
    id: 'mindmap',
    name: 'Mind map',
    description: 'A central topic with a few branches to grow',
    kind: 'WHITEBOARD',
    build: (x, y) => [{ type: 'mindmap', text: 'Central topic', x, y: y + 160, layout: 'both', children: [{ text: 'Branch', children: ['Idea', 'Idea'] }, { text: 'Branch', children: ['Idea'] }, 'Branch', 'Branch'] } as unknown as NodeSpec],
  },
  {
    id: 'icebreaker',
    name: 'Icebreaker',
    description: 'One question, a sticky for everyone',
    kind: 'WHITEBOARD',
    build: (x, y) => [
      {
        type: 'section',
        name: 'Icebreaker',
        x,
        y,
        width: 960,
        height: 560,
        children: [
          { type: 'text', text: 'What’s one thing you’re looking forward to this month?', x: 40, y: 40, width: 880, fontSize: 28, fontWeight: 700 },
          ...['yellow', 'pink', 'blue', 'green', 'violet', 'orange', 'teal', 'yellow'].map((c, i) => ({ ...sticky('', c), x: 40 + (i % 4) * 230, y: i < 4 ? 120 + 0 : 340, width: 200, height: 200 }) as NodeSpec),
        ],
      } as NodeSpec,
    ],
  },
  {
    id: 'mobile-wireframe',
    name: 'Mobile screen',
    description: 'A phone frame with a header, list and tab bar',
    kind: 'DESIGN',
    build: (x, y) => [
      {
        type: 'frame',
        name: 'iPhone 16',
        x,
        y,
        width: 393,
        height: 852,
        fill: '#ffffff',
        layout: 'column',
        children: [
          { type: 'frame', name: 'Header', layout: 'row', width: 'fill', padding: [56, 20, 16, 20], fill: '#ffffff', stroke: '#eeeeee', strokeWidth: [0, 0, 1, 0], children: [{ type: 'text', text: 'Inbox', fontSize: 28, fontWeight: 700 }] },
          {
            type: 'frame',
            name: 'List',
            layout: 'column',
            width: 'fill',
            height: 'fill',
            fill: null,
            children: [0, 1, 2, 3, 4].map((i) => ({
              type: 'frame',
              name: 'Row',
              layout: 'row',
              width: 'fill',
              gap: 12,
              padding: [14, 20],
              crossAlign: 'center',
              stroke: '#f2f2f2',
              strokeWidth: [0, 0, 1, 0],
              children: [
                { type: 'ellipse', width: 40, height: 40, fill: ['#c2e5ff', '#cdf4d3', '#ffd9a8', '#dccbff', '#ffc4f0'][i] },
                { type: 'frame', layout: 'column', gap: 2, width: 'fill', fill: null, children: [{ type: 'text', text: `Item ${i + 1}`, fontSize: 16, fontWeight: 600 }, { type: 'text', text: 'Supporting text', fontSize: 14, color: '#8a8a8a' }] },
              ],
            })),
          },
          { type: 'frame', name: 'Tab bar', layout: 'row', width: 'fill', padding: [12, 32, 34, 32], align: 'space-between', fill: '#fafafa', stroke: '#eeeeee', strokeWidth: [1, 0, 0, 0], children: ['Home', 'Search', 'Profile'].map((t) => ({ type: 'text', text: t, fontSize: 12, fontWeight: 600, color: '#555555' })) },
        ],
      } as NodeSpec,
    ],
  },
  {
    id: 'landing-wireframe',
    name: 'Landing page',
    description: 'Hero, three features and a call to action',
    kind: 'DESIGN',
    build: (x, y) => [
      {
        type: 'frame',
        name: 'Landing page',
        x,
        y,
        width: 1440,
        layout: 'column',
        fill: '#ffffff',
        children: [
          { type: 'frame', name: 'Nav', layout: 'row', width: 'fill', padding: [20, 80], align: 'space-between', crossAlign: 'center', fill: null, children: [{ type: 'text', text: 'Brand', fontSize: 20, fontWeight: 800 }, { type: 'frame', layout: 'row', gap: 32, fill: null, children: ['Product', 'Pricing', 'Docs'].map((t) => ({ type: 'text', text: t, fontSize: 15 })) }] },
          { type: 'frame', name: 'Hero', layout: 'column', width: 'fill', padding: [120, 80], gap: 24, crossAlign: 'center', fill: '#f5f8ff', children: [{ type: 'text', text: 'A headline that says what it does', fontSize: 56, fontWeight: 800, textAlign: 'center' }, { type: 'text', text: 'One sentence about who it’s for and why it matters.', fontSize: 20, color: '#555555', textAlign: 'center' }, { type: 'frame', name: 'Button', layout: 'row', padding: [14, 28], radius: 10, fill: '#0d99ff', children: [{ type: 'text', text: 'Get started', fontSize: 16, fontWeight: 600, color: '#ffffff' }] }] },
          { type: 'frame', name: 'Features', layout: 'row', width: 'fill', padding: [80, 80], gap: 32, fill: null, children: ['Fast', 'Simple', 'Secure'].map((t) => ({ type: 'frame', name: 'Feature', layout: 'column', gap: 12, width: 'fill', padding: 28, radius: 16, fill: '#fafafa', children: [{ type: 'rect', width: 48, height: 48, radius: 12, fill: '#dbeaff' }, { type: 'text', text: t, fontSize: 22, fontWeight: 700 }, { type: 'text', text: 'A short line about this benefit.', fontSize: 16, color: '#666666', width: 'fill' }] })) },
        ],
      } as NodeSpec,
    ],
  },
  {
    id: 'component-sheet',
    name: 'Button components',
    description: 'A button component set with sizes and styles',
    kind: 'DESIGN',
    build: (x, y) => [
      {
        type: 'component_set',
        name: 'Button',
        x,
        y,
        children: (['Primary', 'Secondary'] as const).flatMap((style) =>
          (['Small', 'Large'] as const).map((size) => ({
            type: 'component',
            name: `Style=${style}, Size=${size}`,
            layout: 'row',
            padding: size === 'Small' ? [8, 14] : [14, 24],
            radius: 8,
            fill: style === 'Primary' ? '#0d99ff' : '#ffffff',
            stroke: style === 'Primary' ? undefined : '#d9d9d9',
            children: [{ type: 'text', text: 'Button', exposeText: 'Label', fontSize: size === 'Small' ? 13 : 16, fontWeight: 600, color: style === 'Primary' ? '#ffffff' : '#1e1e1e' }],
          })),
        ),
      } as NodeSpec,
    ],
  },
];

export function findTemplate(ref: string): Template | undefined {
  const r = ref.trim().toLowerCase();
  return TEMPLATES.find((t) => t.id === r || t.name.toLowerCase() === r);
}
